import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

export interface SearchableOption {
    value: string;
    label: string;
    /** Riga secondaria (es. cliente, indirizzo); entra anche nella ricerca */
    sub?: string;
}

interface Props {
    value: string;
    onChange: (value: string) => void;
    options: SearchableOption[];
    placeholder: string;
    searchPlaceholder?: string;
    emptyText?: string;
    disabled?: boolean;
    /** Classi del pulsante chiuso (bordo, focus) per restare nello stile del form */
    className?: string;
}

/**
 * Select con campo di ricerca. L'elenco si apre sotto il campo nel flusso della
 * pagina, così non viene tagliato dai modali con overflow nascosto.
 */
const SearchableSelect: React.FC<Props> = ({
    value, onChange, options, placeholder, searchPlaceholder = 'Cerca…', emptyText = 'Nessun risultato',
    disabled = false, className = '',
}) => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const selected = options.find(o => o.value === value);
    const filtered = useMemo(() => {
        const t = q.trim().toLowerCase();
        if (!t) return options;
        return options.filter(o => `${o.label} ${o.sub || ''}`.toLowerCase().includes(t));
    }, [options, q]);

    useEffect(() => {
        if (!open) return;
        inputRef.current?.focus();
        const onDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    const scegli = (v: string) => { onChange(v); setOpen(false); setQ(''); };

    return (
        <div ref={rootRef}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen(o => !o)}
                className={`w-full p-3 border rounded-xl bg-white font-medium flex items-center justify-between gap-2 text-left disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
            >
                <span className={`truncate ${selected ? 'text-gray-900' : 'text-gray-500'}`}>{selected ? selected.label : placeholder}</span>
                <ChevronDown size={18} className={`shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="mt-1 border border-gray-200 rounded-xl bg-white shadow-lg overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                        <Search size={15} className="text-gray-400 shrink-0" />
                        <input
                            ref={inputRef}
                            className="w-full text-sm outline-none bg-transparent"
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            placeholder={searchPlaceholder}
                            onKeyDown={e => {
                                if (e.key === 'Escape') setOpen(false);
                                if (e.key === 'Enter') { e.preventDefault(); if (filtered.length === 1) scegli(filtered[0].value); }
                            }}
                        />
                    </div>
                    <ul className="max-h-60 overflow-y-auto py-1" role="listbox">
                        {filtered.length === 0 ? (
                            <li className="px-3 py-2 text-sm text-gray-400">{emptyText}</li>
                        ) : filtered.map(o => {
                            const attivo = o.value === value;
                            return (
                                <li key={o.value} role="option" aria-selected={attivo}>
                                    <button
                                        type="button"
                                        onClick={() => scegli(o.value)}
                                        className={`w-full px-3 py-2 text-left flex items-center justify-between gap-2 hover:bg-gray-50 ${attivo ? 'bg-blue-50' : ''}`}
                                    >
                                        <span className="min-w-0">
                                            <span className="block text-sm font-medium text-gray-900 truncate">{o.label}</span>
                                            {o.sub && <span className="block text-xs text-gray-500 truncate">{o.sub}</span>}
                                        </span>
                                        {attivo && <Check size={16} className="text-blue-600 shrink-0" />}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
